import { FC } from "react";
import { Link } from "react-router-dom";
import { useData } from "data/data-provider";
import moment from "moment";
import { companyInitials, companyName as companyNameOf } from "@1-now/booking-core";

/**
 * Design B brand mark.
 *
 * Renders the FMS-uploaded company logo when available; otherwise falls back
 * to a clean wordmark + car icon so the header never looks naked when a
 * company hasn't uploaded a logo yet. Mirrors `shared/Logo/Logo` API
 * (uses `companyDetails.company_logo` and `setSelectedWeeks` reset on click).
 *
 * `compact` (1NOW-11192) is the brand in the header while the search shows, left of the search:
 * the same logo image, or the same tile and name, at a size that leaves the search its room. The
 * tile stays; the name shows only between lg and xl, where the search is a pill and there is room,
 * and the image is capped so it cannot push the search around. On phones it is a small mark
 * (an image 40px wide at 320, 56px from 360, a 36px tile) so the pill beside it keeps a whole line of text.
 */
const Logo: FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { companyDetails, setSelectedWeeks } = useData();
  const time = moment();
  const timeInMilliseconds = time.valueOf();

  const companyLogoUrl = companyDetails?.company_logo
    ? `${companyDetails.company_logo}?date=${timeInMilliseconds}`
    : "";

  const companyName: string = companyNameOf(companyDetails);

  // First letter or two for the badge fallback
  const initials = companyInitials(companyName);

  const handleClick = () => {
    setSelectedWeeks([]);
  };

  return (
    // No explicit text color on the wrapper — the Logo INHERITS from its
    // parent's text color. That way:
    //   - In the Header (sits inside `text-text-color` page wrapper) the
    //     wordmark renders in navy on the cream banner.
    //   - In the Footer (`text-footer-text-color`) the wordmark renders in
    //     cream on the dark navy footer, instead of disappearing into the
    //     bg as it did when this was hardcoded to `text-text-color`.
    <Link
      to="/"
      onClick={handleClick}
      className={`ttnc-logo group inline-flex items-center gap-3 focus:outline-none focus:ring-0 ${
        compact ? "shrink-0" : "min-w-0"
      }`}
      aria-label={companyName || "Home"}
    >
      {companyLogoUrl ? (
        <img
          src={companyLogoUrl}
          alt={companyName ? `${companyName} logo` : "Logo"}
          className={`object-contain transition-transform duration-300 group-hover:scale-[1.03] ${
            compact
              ? "h-8 w-auto max-h-8 max-w-[2.5rem] min-[360px]:max-w-[3.5rem] sm:max-w-[7rem] lg:max-w-[10rem] xl:max-w-[6rem]"
              : "h-8 w-auto max-h-8 max-w-[120px] sm:h-10 sm:max-h-10 sm:max-w-[180px]"
          }`}
        />
      ) : (
        <>
          {/* Brand tile in the primary colour: the initials, else a car mark */}
          <span
            className={`flex shrink-0 items-center justify-center rounded-[10px] bg-primary-600 text-on-primary transition-transform duration-300 group-hover:scale-105 ${
              compact ? "h-9 w-9 sm:h-10 sm:w-10" : "h-10 w-10"
            }`}
          >
            {initials ? (
              <span className="text-sm font-black tracking-tight">{initials}</span>
            ) : (
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {/* Car silhouette: body + cabin, with two wheels */}
                <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2" />
                <circle cx="6.5" cy="16.5" r="2.5" />
                <circle cx="16.5" cy="16.5" r="2.5" />
              </svg>
            )}
          </span>
          {/* Company name only, in the bold uppercase wordmark. No explicit colour: it
              inherits the surrounding bar's (the header's and the footer's own). */}
          {companyName && (
            <span
              className={`truncate text-sm font-black uppercase leading-tight tracking-[0.05em] ${
                compact ? "hidden max-w-[9rem] lg:block xl:hidden" : "max-w-[46vw] sm:max-w-[16rem] sm:text-base"
              }`}
            >
              {companyName}
            </span>
          )}
        </>
      )}
    </Link>
  );
};

export default Logo;
